import { supabase } from './client'

const NOTE_IMAGE_BUCKET_NAME = 'DevTracker'
const TASK_ATTACHMENT_BUCKET_NAME = 'DevTracker'

const TASK_ATTACHMENT_ALLOWED_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'eml',
])

const TASK_ATTACHMENT_ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/csv',
  'message/rfc822',
  'application/octet-stream',
])

const sanitizeFilename = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-')

const getFileExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()
  return extension ? extension.toLowerCase() : ''
}

const createStorageFilename = (fileName: string) => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).slice(2, 10)
  return `${timestamp}-${random}-${sanitizeFilename(fileName)}`
}

export interface UploadedTaskAttachment {
  fileName: string
  storagePath: string
  mimeType: string | null
  fileSizeBytes: number
}

export const isTaskAttachmentFileAllowed = (file: File) => {
  const extension = getFileExtension(file.name)
  if (!TASK_ATTACHMENT_ALLOWED_EXTENSIONS.has(extension)) {
    return false
  }

  if (!file.type) {
    return true
  }

  return TASK_ATTACHMENT_ALLOWED_MIME_TYPES.has(file.type)
}

export const getTaskAttachmentValidationMessage = (file: File) => {
  if (isTaskAttachmentFileAllowed(file)) {
    return null
  }

  return 'Unsupported file type. Allowed files: images, Word, Excel, CSV, and EML.'
}

export const uploadTaskAttachment = async (taskId: string, file: File): Promise<UploadedTaskAttachment> => {
  const validationMessage = getTaskAttachmentValidationMessage(file)
  if (validationMessage) {
    throw new Error(validationMessage)
  }

  const storagePath = `attachments/${taskId}/${createStorageFilename(file.name)}`
  const { error } = await supabase.storage.from(TASK_ATTACHMENT_BUCKET_NAME).upload(storagePath, file)

  if (error) {
    throw new Error(`Failed to upload attachment: ${error.message}`)
  }

  return {
    fileName: file.name,
    storagePath,
    mimeType: file.type || null,
    fileSizeBytes: file.size,
  }
}

export const getTaskAttachmentSignedUrl = async (storagePath: string): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(TASK_ATTACHMENT_BUCKET_NAME)
    .createSignedUrl(storagePath, 60)

  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'Failed to create attachment access URL.')
  }

  return data.signedUrl
}

/**
 * Upload an image file to Supabase storage
 * @param file - The image file to upload
 * @param folder - Optional folder path (e.g., 'tasks', 'notes')
 * @returns Public URL of the uploaded image
 */
export const uploadImage = async (file: File, folder: string = 'images'): Promise<string> => {
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image')
  }

  // Generate unique filename
  const filename = createStorageFilename(file.name)
  const filepath = `${folder}/${filename}`

  // Upload file
  const { error: uploadError } = await supabase.storage.from(NOTE_IMAGE_BUCKET_NAME).upload(filepath, file)

  if (uploadError) {
    throw new Error(`Failed to upload image: ${uploadError.message}`)
  }

  // Get public URL
  const { data } = supabase.storage.from(NOTE_IMAGE_BUCKET_NAME).getPublicUrl(filepath)
  
  if (!data.publicUrl) {
    throw new Error('Failed to get public URL for uploaded image')
  }

  return data.publicUrl
}

/**
 * Delete an image from storage
 * @param imageUrl - The public URL of the image
 */
export const deleteImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extract filepath from URL
    const url = new URL(imageUrl)
    const filepath = url.pathname.split('/storage/v1/object/public/DevTracker/')[1]
    
    if (!filepath) {
      console.warn('Could not extract filepath from image URL:', imageUrl)
      return
    }

    const { error } = await supabase.storage.from(NOTE_IMAGE_BUCKET_NAME).remove([filepath])
    
    if (error) {
      console.error('Failed to delete image:', error)
    }
  } catch (err) {
    console.error('Error deleting image:', err)
  }
}
